import React, { useState } from 'react';
import AuthLayout from './components/AuthLayout';
import { useAuth } from './utils/useAuth';
import { getApiUrl } from './utils/apiConfig';
import { ENABLE_EMAIL_AUTH } from './utils/authConfig';

const cardOuter = (theme) => ({
  width: '100%',
  maxWidth: 460,
  margin: '0 auto',
  padding: 28,
  background: theme.cardBg,
  border: `1px solid ${theme.border}`,
  borderRadius: 20,
  boxShadow: theme.shadow
});

/**
 * Validates user_id according to rules:
 * - Only letters (a-z, A-Z), numbers (0-9), and hyphens (-)
 * - Cannot start or end with a hyphen
 * - Cannot contain two consecutive hyphens
 */
function validateUserId(userId) {
  if (!userId || userId.trim().length === 0) {
    return 'User ID is required';
  }
  if (!/^[a-zA-Z0-9-]+$/.test(userId)) {
    return 'User ID can only contain letters (a-z, A-Z), numbers (0-9), and hyphens (-)';
  }
  if (userId.startsWith('-')) {
    return 'User ID cannot start with a hyphen';
  }
  if (userId.endsWith('-')) {
    return 'User ID cannot end with a hyphen';
  }
  if (userId.includes('--')) {
    return 'User ID cannot contain two consecutive hyphens';
  }
  if (userId.length < 3) {
    return 'User ID must be at least 3 characters long';
  }
  if (userId.length > 30) {
    return 'User ID must be at most 30 characters long';
  }
  return null;
}

function buildSignupFormStyles(theme, darkMode) {
  const buttonBase = {
    appearance: 'none',
    border: `1px solid ${theme.border2}`,
    borderRadius: 12,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    color: theme.text,
    background: 'transparent',
    cursor: 'pointer'
  };
  const inputBase = {
    width: '100%',
    background: theme.inputBg,
    border: `1px solid ${theme.border2}`,
    color: theme.text,
    borderRadius: 12,
    padding: '12px 14px',
    outline: 'none',
    fontSize: 14,
    boxSizing: 'border-box'
  };
  return {
    formTitle: {
      margin: 0,
      fontSize: 22,
      fontWeight: 950,
      letterSpacing: '-0.03em'
    },
    formSubtitle: {
      margin: '8px 0 0',
      color: theme.muted,
      fontSize: 14,
      lineHeight: 1.65
    },
    alert: {
      marginTop: 14,
      border: `1px solid ${theme.alertBorder}`,
      background: theme.alertBg,
      color: theme.alertText,
      borderRadius: 14,
      padding: '10px 12px',
      fontSize: 13,
      lineHeight: 1.5
    },
    successBox: {
      marginTop: 14,
      border: `1px solid rgba(34, 197, 94, 0.35)`,
      background: darkMode ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.14)',
      color: darkMode ? '#86EFAC' : '#15803D',
      borderRadius: 14,
      padding: '12px 14px',
      fontSize: 14,
      lineHeight: 1.55
    },
    infoBox: {
      marginTop: 14,
      border: `1px solid ${theme.border}`,
      background: theme.codeBlockBg,
      color: theme.text,
      borderRadius: 14,
      padding: '12px 14px',
      fontSize: 14,
      lineHeight: 1.55
    },
    form: {
      marginTop: 16,
      display: 'grid',
      gap: 12
    },
    label: {
      display: 'grid',
      gap: 8,
      fontSize: 13,
      fontWeight: 700,
      color: theme.text
    },
    help: {
      marginTop: 6,
      fontSize: 12,
      color: theme.muted,
      lineHeight: 1.45
    },
    input: inputBase,
    inputError: {
      ...inputBase,
      borderColor: 'rgba(248, 113, 113, 0.55)'
    },
    fieldError: {
      marginTop: 6,
      color: theme.fieldErrorColor,
      fontSize: 12,
      lineHeight: 1.35
    },
    btnPrimary: {
      ...buttonBase,
      borderColor: darkMode ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.4)',
      background: darkMode
        ? 'linear-gradient(180deg, rgba(96, 165, 250, 0.24), rgba(59, 130, 246, 0.16))'
        : 'linear-gradient(180deg, #3B82F6, #2563EB)',
      color: theme.btnPrimaryColor,
      width: '100%',
      padding: '12px 14px'
    },
    subtleLinkBtn: {
      ...buttonBase,
      width: '100%',
      marginTop: 12,
      background: 'transparent',
      borderColor: theme.border,
      color: theme.muted
    }
  };
}

function Signup() {
  const auth = useAuth();
  const [formValue, setFormValue] = useState({
    email: '',
    password: '',
    user_id: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async () => {
    setErrors({});
    setErrorMessage('');

    const userIdError = validateUserId(formValue.user_id);
    if (userIdError) {
      setErrors({ user_id: userIdError });
      return;
    }
    if (!formValue.email || !formValue.email.includes('@')) {
      setErrors({ email: 'Valid email is required' });
      return;
    }
    if (!formValue.password || formValue.password.length < 8) {
      setErrors({ password: 'Password must be at least 8 characters long' });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', formValue.email);
      formData.append('password', formValue.password);
      formData.append('user_id', formValue.user_id);
      formData.append('connection', 'Username-Password-Authentication');

      const response = await fetch(getApiUrl('/api/users'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail?.message || data.detail || 'Failed to create user');
      }

      setSuccess(true);
      setErrorMessage('');

      setTimeout(() => {
        if (auth.loginWithRedirect) {
          auth.loginWithRedirect();
        }
      }, 2000);
    } catch (err) {
      console.error('Signup error:', err);
      setErrorMessage(err.message || 'Failed to create account. Please try again.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const navRight = (shellStyles) => (
    <>
      <button type="button" style={shellStyles.btnGhost} onClick={() => { window.location.hash = '#/'; }}>
        Home
      </button>
      <button type="button" style={shellStyles.btnPrimary} onClick={() => { window.location.hash = '#/login'; }}>
        Log in
      </button>
    </>
  );

  const cardShell = (contentFn) => ({ theme, darkMode, shellStyles }) => {
    const fs = buildSignupFormStyles(theme, darkMode);
    return (
      <div style={shellStyles.pageInner}>
        <div style={cardOuter(theme)}>{contentFn(fs)}</div>
      </div>
    );
  };

  if (success) {
    return (
      <AuthLayout brandTitle="All Completed Tuning" navRightSlot={navRight}>
        {cardShell((fs) => (
          <div aria-label="Sign up success">
            <h1 style={fs.formTitle}>Account created</h1>
            <p style={fs.formSubtitle}>Redirecting you to sign in…</p>
            <div style={fs.successBox}>Your account is ready. You&apos;ll be sent to Auth0 in a moment.</div>
          </div>
        ))}
      </AuthLayout>
    );
  }

  if (!ENABLE_EMAIL_AUTH) {
    return (
      <AuthLayout brandTitle="All Completed Tuning" navRightSlot={navRight}>
        {cardShell((fs) => (
          <div aria-label="Sign up disabled">
            <h1 style={fs.formTitle}>Sign up</h1>
            <p style={fs.formSubtitle}>Email signup is not enabled on this deployment.</p>
            <div style={fs.infoBox}>
              Use Google, GitHub, Apple, or Microsoft from the login page when configured.
            </div>
            <button type="button" style={{ ...fs.btnPrimary, marginTop: 16 }} onClick={() => { window.location.hash = '#/login'; }}>
              Go to login
            </button>
          </div>
        ))}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout brandTitle="All Completed Tuning" navRightSlot={navRight}>
      {cardShell((fs) => (
        <div aria-label="Sign up form">
          <h1 style={fs.formTitle}>Create account</h1>
          <p style={fs.formSubtitle}>Pick a unique user id for jobs and APIs, then choose email and password.</p>

          {errorMessage ? <div style={fs.alert}>{errorMessage}</div> : null}

          <form
            style={fs.form}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div>
              <label style={fs.label} htmlFor="signup-email">
                Email
                <input
                  id="signup-email"
                  autoComplete="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formValue.email}
                  onChange={(e) => setFormValue((v) => ({ ...v, email: e.target.value }))}
                  style={errors.email ? fs.inputError : fs.input}
                  disabled={loading}
                />
              </label>
              {errors.email ? <div style={fs.fieldError}>{errors.email}</div> : null}
            </div>

            <div>
              <label style={fs.label} htmlFor="signup-user-id">
                User ID
                <input
                  id="signup-user-id"
                  autoComplete="username"
                  placeholder="e.g. john-doe123"
                  value={formValue.user_id}
                  onChange={(e) => setFormValue((v) => ({ ...v, user_id: e.target.value }))}
                  style={errors.user_id ? fs.inputError : fs.input}
                  disabled={loading}
                />
              </label>
              <div style={fs.help}>
                Letters, numbers, and hyphens only. Cannot start/end with hyphen or use consecutive hyphens.
              </div>
              {errors.user_id ? <div style={fs.fieldError}>{errors.user_id}</div> : null}
            </div>

            <div>
              <label style={fs.label} htmlFor="signup-password">
                Password
                <input
                  id="signup-password"
                  autoComplete="new-password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={formValue.password}
                  onChange={(e) => setFormValue((v) => ({ ...v, password: e.target.value }))}
                  style={errors.password ? fs.inputError : fs.input}
                  disabled={loading}
                />
              </label>
              {errors.password ? <div style={fs.fieldError}>{errors.password}</div> : null}
            </div>

            <button type="submit" style={fs.btnPrimary} disabled={loading}>
              {loading ? 'Creating…' : 'Sign up'}
            </button>
          </form>

          <button
            type="button"
            style={fs.subtleLinkBtn}
            disabled={loading}
            onClick={() => {
              window.location.hash = '#/login';
            }}
          >
            Already have an account? Log in
          </button>
        </div>
      ))}
    </AuthLayout>
  );
}

export default Signup;
